import createHash from 'create-hash'
import type { JwtPayload } from 'jwt-decode'
import jwt_decode from 'jwt-decode'
import randomBytes from 'randombytes'

import { localStorageKeys } from '../reference'
import { getNavGroups } from './nav'

type UserProfile = JwtPayload & {
  at_hash?: string
  auth_time?: number
  'cognito:groups'?: string[]
  'cognito:username'?: string
  email?: string
  email_verified?: boolean
  event_id?: string
  family_name?: string
  given_name?: string
  token_use?: string
}

type AuthTokens = {
  accessToken: string
  idToken: string | null
  refreshToken: string | null
}

const SCOPE = 'email+openid+profile+aws.cognito.signin.user.admin'
const ACCESS_TOKEN_REFRESH_BUFFER_SECONDS = 5 * 60

const decodeToken = <T extends JwtPayload>(token: string | null | undefined): T | null => {
  if (!token) return null

  try {
    return jwt_decode<T>(token)
  } catch {
    return null
  }
}

const readStoredTokens = (): AuthTokens | null => {
  const accessToken = window.localStorage.getItem(localStorageKeys.cls_plus_admin_accessToken)
  const idToken = window.localStorage.getItem(localStorageKeys.cls_plus_admin_idToken)
  const refreshToken = window.localStorage.getItem(localStorageKeys.cls_plus_admin_refreshToken)

  if (accessToken === null) {
    return null
  }

  return {
    accessToken,
    idToken,
    refreshToken,
  }
}

let tokens = readStoredTokens()
let userProfile = decodeToken<UserProfile>(tokens?.idToken)
let refreshTokensPromise: Promise<AuthTokens | null> | null = null

const syncAuthStateFromStorage = (): AuthTokens | null => {
  tokens = readStoredTokens()
  userProfile = decodeToken<UserProfile>(tokens?.idToken)
  return tokens
}

const persistTokens = (nextTokens: AuthTokens | null) => {
  if (nextTokens?.accessToken) {
    window.localStorage.setItem(localStorageKeys.cls_plus_admin_accessToken, nextTokens.accessToken)
  } else {
    window.localStorage.removeItem(localStorageKeys.cls_plus_admin_accessToken)
  }

  if (nextTokens?.idToken) {
    window.localStorage.setItem(localStorageKeys.cls_plus_admin_idToken, nextTokens.idToken)
  } else {
    window.localStorage.removeItem(localStorageKeys.cls_plus_admin_idToken)
  }

  if (nextTokens?.refreshToken) {
    window.localStorage.setItem(localStorageKeys.cls_plus_admin_refreshToken, nextTokens.refreshToken)
  } else {
    window.localStorage.removeItem(localStorageKeys.cls_plus_admin_refreshToken)
  }

  tokens = nextTokens
  userProfile = decodeToken<UserProfile>(nextTokens?.idToken)
}

const shouldRefreshAccessToken = (accessToken: string) => {
  const decodedToken = decodeToken<UserProfile>(accessToken)

  if (!decodedToken?.exp) {
    return true
  }

  const refreshAt = Math.floor(Date.now() / 1000) + ACCESS_TOKEN_REFRESH_BUFFER_SECONDS
  return decodedToken.exp <= refreshAt
}

const base64URLEncode = (str: Buffer) => {
  return str.toString('base64').replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

const sha256 = (buffer: Buffer | string) => {
  return createHash('sha256').update(buffer).digest()
}

let storedVerifier = window.localStorage.getItem('cls_plus_admin_verifier')
if (storedVerifier === null) {
  const newVerifier = base64URLEncode(randomBytes(32))
  window.localStorage.setItem('cls_plus_admin_verifier', newVerifier)
  storedVerifier = newVerifier
}
const verifier = storedVerifier
const challenge = base64URLEncode(sha256(storedVerifier))
const baseUrl = import.meta.env.VITE_AUTH_BASE_URL
const clientId = import.meta.env.VITE_AUTH_CLIENT_ID
const redirectUri = import.meta.env.VITE_AUTH_REDIRECT_URI
const tokenUrl = `${baseUrl}/oauth2/token`

// eslint-disable-next-line max-len
export const loginUrl = `${baseUrl}/login?scope=${SCOPE}&response_type=code&client_id=${clientId}&code_challenge=${challenge}&code_challenge_method=S256&redirect_uri=${redirectUri}`

const performTokenRefresh = async (refreshToken: string): Promise<AuthTokens | null> => {
  const currentTokens = syncAuthStateFromStorage()
  const body = `grant_type=refresh_token&client_id=${clientId}&refresh_token=${refreshToken}`
  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  if (!response.ok) {
    // basically if the refresh did not work, we should log them out
    logout()
    return null
  }

  const { access_token, id_token } = await response.json()

  if (typeof access_token !== 'string' || access_token.length === 0) {
    logout()
    return null
  }

  const nextTokens: AuthTokens = {
    accessToken: access_token,
    idToken: typeof id_token === 'string' ? id_token : currentTokens?.idToken ?? null,
    refreshToken,
  }

  persistTokens(nextTokens)
  return nextTokens
}

const refreshTokens = async (refreshToken: string) => {
  if (!refreshTokensPromise) {
    refreshTokensPromise = performTokenRefresh(refreshToken).finally(() => {
      refreshTokensPromise = null
    })
  }

  return await refreshTokensPromise
}

export const ensureFreshTokens = async ({ forceRefresh = false }: { forceRefresh?: boolean } = {}) => {
  const currentTokens = syncAuthStateFromStorage()

  if (!currentTokens?.accessToken) {
    return null
  }

  if (!currentTokens.refreshToken) {
    return currentTokens
  }

  if (!forceRefresh && !shouldRefreshAccessToken(currentTokens.accessToken)) {
    return currentTokens
  }

  return await refreshTokens(currentTokens.refreshToken)
}

export const login = async (code: string) => {
  // eslint-disable-next-line max-len
  const body = `grant_type=authorization_code&client_id=${clientId}&code_verifier=${verifier}&code=${code}&redirect_uri=${redirectUri}`
  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  if (!response.ok) {
    throw Error()
  }

  const { access_token, id_token, refresh_token } = await response.json()

  if (typeof access_token !== 'string' || access_token.length === 0) {
    throw Error()
  }

  const nextTokens: AuthTokens = {
    accessToken: access_token,
    idToken: typeof id_token === 'string' ? id_token : null,
    refreshToken: typeof refresh_token === 'string' ? refresh_token : null,
  }

  persistTokens(nextTokens)
}

export const logout = async () => {
  persistTokens(null)
  window.localStorage.removeItem(localStorageKeys.cls_plus_admin_live_monitoring)
}

export const checkTokens = async (accessToken: string | null, refreshToken: string | null) => {
  if (!accessToken || !refreshToken) {
    return false
  }

  // Bypass login checks when e2e tests are running
  if (process.env.PLAYWRIGHT_ACTIVE) return true

  const result = await ensureFreshTokens()
  return !!result?.accessToken
}

export const getTokens = () => syncAuthStateFromStorage()
export const getUserProfile = () => userProfile

export type MCRequiredLevel = 'locked' | 'clsp-monitoring-users' | 'cpa-admins' | 'clsp-superadmin'
export enum MCRequiredLevelEnum {
  locked = 0,
  'clsp-monitoring-users' = 1,
  'cpa-admins' = 2,
  'clsp-superadmin' = 3,
}

const checkAuthLevel = (): MCRequiredLevelEnum => {
  const cognitoGroups = getUserProfile()?.['cognito:groups'] as Array<MCRequiredLevel> | undefined

  const acquiredLevels: Array<MCRequiredLevelEnum> = []

  for (const key of Object.keys(MCRequiredLevelEnum) as Array<MCRequiredLevel>) {
    if (cognitoGroups?.includes(key)) {
      acquiredLevels.push(MCRequiredLevelEnum[key])
    }
  }

  return acquiredLevels.sort()[acquiredLevels.length - 1] ?? MCRequiredLevelEnum.locked
}

export const CurrentAccessLevel = () => {
  return window.localStorage.getItem('AccessOverrideLevel')
    ? (Number(window.localStorage.getItem('AccessOverrideLevel')) as MCRequiredLevelEnum)
    : checkAuthLevel()
}

export const HasMCAccess = ({ children, minLevel }: { children: JSX.Element; minLevel: MCRequiredLevelEnum }) => {
  if (CurrentAccessLevel() >= minLevel) {
    return children
  }

  return null
}

export const getNavGroupAccess = () => {
  const navGroupMap = new Map<string, MCRequiredLevelEnum>()

  for (const nvGroup of getNavGroups()) {
    for (const nvItem of nvGroup.items) {
      navGroupMap.set(nvItem.href, nvItem.requiredAccess)
    }
  }

  return navGroupMap
}
